import { useCallback, useEffect, useMemo, useState } from "react";
import actions, { KYDUserWithGuestlist } from "../../../actions";
import { useLoading } from "../../hooks/form";
import { useDialog } from "../../../Common/Dialog";
import { KYDGuestlistDisplayState } from "@common/interfaces/KYDEvent";
import { countGuestlistTags } from "@common/utils/guestlistTagCounts";

type GuestlistAllocation = KYDGuestlistDisplayState;

export const useGuestlist = (
  event_id: string,
  options?: {
    autoFetch?: boolean;
    initialData?: {
      users?: KYDUserWithGuestlist[];
      allocation?: KYDGuestlistDisplayState;
    };
  }
) => {
  const [loading, setLoading] = useLoading<{ allocation: boolean }>({
    allocation: false,
  });
  const [guestlist, setGuestlist] = useState<GuestlistAllocation | null>(
    options?.initialData?.allocation || {
      allocations: [
        {
          allocation: 0,
          remaining: 0,
          assigned: 0,
          ticket_type_id: "",
          is_processing: false,
        },
      ],
      unique_people: 0,
    }
  );
  const [users, setUsers] = useState<KYDUserWithGuestlist[]>(
    options?.initialData?.users || []
  );
  const tagCounts = useMemo(() => countGuestlistTags(users), [users]);
  const openMessageDialog = useDialog();
  const autoFetch = options?.autoFetch !== false;

  const updateGuestlistAllocation = useCallback(
    async (allocation: KYDGuestlistDisplayState) => {
      setLoading("allocation", true);
      try {
        const data = await actions.ADMIN.updateGuestlistAllocation({
          event_id: event_id,
          allocation,
        });

        if (data.statusCode !== 200) {
          return Promise.reject(data.message);
        }

        await fetchGuestlist(event_id, { silent: true });

        console.log({ onGuestlistAllocationChange: data });
      } catch (e) {
        openMessageDialog({
          title: "Error",
          text: e.message,
        });
      } finally {
        setLoading("allocation", false);
      }
    },
    [guestlist]
  );

  const fetchGuestlist = useCallback(
    async (eventId: string, options?: { silent?: boolean }) => {
      try {
        if (!eventId) return;

        if (!options?.silent) {
          setLoading("allocation", true);
        }
        const data = await actions.ADMIN.fetchGuestlist(eventId);

        if (data.allocation) {
          setGuestlist(data.allocation);
        }

        if (data.users) {
          setUsers(data.users);
        }
      } catch (err) {
        openMessageDialog({
          title: "Error",
          text: err.message,
        });
      } finally {
        if (!options?.silent) {
          setLoading("allocation", false);
        }
      }
    },
    [event_id]
  );

  useEffect(() => {
    if (options?.initialData?.allocation) {
      setGuestlist(options.initialData.allocation);
    }
    if (options?.initialData?.users) {
      setUsers(options.initialData.users);
    }
  }, [options?.initialData?.allocation, options?.initialData?.users]);

  useEffect(() => {
    if (event_id && autoFetch) {
      fetchGuestlist(event_id);
    }
  }, [event_id, autoFetch, fetchGuestlist]);

  return {
    refetch: (options?: { silent?: boolean }) =>
      fetchGuestlist(event_id, options),
    updateGuestlistAllocation,
    guestlist,
    users,
    tagCounts,
    loading: loading.allocation,
    cache: {
      setGuestlist,
      setUsers,
    },
  };
};
